import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Building2,
  Save,
  Upload,
  Shield,
  Delete,
  CheckCircle,
  XCircle,
  AlertCircle,
  Zap,
  Loader2,
  Palette,
  FileText,
  Settings,
  Hash,
  Eye,
  EyeOff,
  Image,
  ArrowLeft,
  Search,
  Mail,
  Send,
  Clock,
  Plug,
  Bell,
  ExternalLink
} from 'lucide-react';
import useAuthStore from '../../store/authStore';
import { settingsApi, emisoresApi, notificationsApi } from '../../utils/api';
import { getDistritosOptions } from '../../utils/costaRicaLocations';
import ConfirmModal from '../../components/ConfirmModal';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import InvoiceTemplate from '../../components/InvoiceTemplate';
import RecepcionFacturas from '../../components/configuracion/RecepcionFacturas';

const TABS = [
  { id: 'general', label: 'Datos Generales', icon: Building2 },
  { id: 'diseno', label: 'Logo y Diseño', icon: Palette },
  { id: 'terminos', label: 'Términos y Condiciones', icon: FileText },
  { id: 'hacienda', label: 'Hacienda (TribuCR)', icon: Settings },
  { id: 'recepcion', label: 'Facturas recibidas', icon: FileText },
  { id: 'certificados', label: 'Certificados', icon: Shield },
  { id: 'consecutivos', label: 'Consecutivos', icon: Hash },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'sistema', label: 'Sistema', icon: Clock },
  { id: 'integraciones', label: 'Integraciones', icon: Plug },
  { id: 'notificaciones', label: 'Notificaciones', icon: Bell },
];

const PAISES = [
  { value: 'CR', label: 'Costa Rica', flag: '🇨🇷' },
  { value: 'PA', label: 'Panamá', flag: '🇵🇦' },
  { value: 'NI', label: 'Nicaragua', flag: '🇳🇮' },
  { value: 'HN', label: 'Honduras', flag: '🇭🇳' },
  { value: 'SV', label: 'El Salvador', flag: '🇸🇻' },
  { value: 'GT', label: 'Guatemala', flag: '🇬🇹' },
  { value: 'MX', label: 'México', flag: '🇲🇽' },
  { value: 'CO', label: 'Colombia', flag: '🇨🇴' },
  { value: 'US', label: 'Estados Unidos', flag: '🇺🇸' },
  { value: 'ES', label: 'España', flag: '🇪🇸' },
];

const PROVINCIAS = [
  { value: '1', label: 'San José' },
  { value: '2', label: 'Alajuela' },
  { value: '3', label: 'Cartago' },
  { value: '4', label: 'Heredia' },
  { value: '5', label: 'Guanacaste' },
  { value: '6', label: 'Puntarenas' },
  { value: '7', label: 'Limón' },
];

const CANTONES = {
  '1': [ // San José
    { value: '01', label: 'San José' }, { value: '02', label: 'Escazú' }, { value: '03', label: 'Desamparados' },
    { value: '04', label: 'Puriscal' }, { value: '05', label: 'Tarrazú' }, { value: '06', label: 'Aserrí' },
    { value: '07', label: 'Mora' }, { value: '08', label: 'Goicoechea' }, { value: '09', label: 'Santa Ana' },
    { value: '10', label: 'Alajuelita' }, { value: '11', label: 'Vázquez de Coronado' }, { value: '12', label: 'Acosta' },
    { value: '13', label: 'Tibás' }, { value: '14', label: 'Moravia' }, { value: '15', label: 'Montes de Oca' },
    { value: '16', label: 'Turrubares' }, { value: '17', label: 'Dota' }, { value: '18', label: 'Curridabat' },
    { value: '19', label: 'Pérez Zeledón' }, { value: '20', label: 'León Cortés' },
  ],
  '2': [ // Alajuela
    { value: '01', label: 'Alajuela' }, { value: '02', label: 'San Ramón' }, { value: '03', label: 'Grecia' },
    { value: '04', label: 'San Mateo' }, { value: '05', label: 'Atenas' }, { value: '06', label: 'Naranjo' },
    { value: '07', label: 'Palmares' }, { value: '08', label: 'Poás' }, { value: '09', label: 'Orotina' },
    { value: '10', label: 'San Carlos' }, { value: '11', label: 'Zarcero' }, { value: '12', label: 'Sarchí' },
    { value: '13', label: 'Upala' }, { value: '14', label: 'Los Chiles' }, { value: '15', label: 'Guatuso' },
    { value: '16', label: 'Río Cuarto' },
  ],
  '3': [ // Cartago
    { value: '01', label: 'Cartago' }, { value: '02', label: 'Paraíso' }, { value: '03', label: 'La Unión' },
    { value: '04', label: 'Jiménez' }, { value: '05', label: 'Turrialba' }, { value: '06', label: 'Alvarado' },
    { value: '07', label: 'Oreamuno' }, { value: '08', label: 'El Guarco' },
  ],
  '4': [ // Heredia
    { value: '01', label: 'Heredia' }, { value: '02', label: 'Barva' }, { value: '03', label: 'Santo Domingo' },
    { value: '04', label: 'Santa Bárbara' }, { value: '05', label: 'San Rafael' }, { value: '06', label: 'San Isidro' },
    { value: '07', label: 'Belén' }, { value: '08', label: 'Flores' }, { value: '09', label: 'San Pablo' },
    { value: '10', label: 'Sarapiquí' },
  ],
  '5': [ // Guanacaste
    { value: '01', label: 'Liberia' }, { value: '02', label: 'Nicoya' }, { value: '03', label: 'Santa Cruz' },
    { value: '04', label: 'Bagaces' }, { value: '05', label: 'Carrillo' }, { value: '06', label: 'Cañas' },
    { value: '07', label: 'Abangares' }, { value: '08', label: 'Tilarán' }, { value: '09', label: 'Nandayure' },
    { value: '10', label: 'La Cruz' }, { value: '11', label: 'Hojancha' },
  ],
  '6': [ // Puntarenas
    { value: '01', label: 'Puntarenas' }, { value: '02', label: 'Esparza' }, { value: '03', label: 'Buenos Aires' },
    { value: '04', label: 'Montes de Oro' }, { value: '05', label: 'Osa' }, { value: '06', label: 'Quepos' },
    { value: '07', label: 'Golfito' }, { value: '08', label: 'Coto Brus' }, { value: '09', label: 'Parrita' },
    { value: '10', label: 'Corredores' }, { value: '11', label: 'Garabito' },
  ],
  '7': [ // Limón
    { value: '01', label: 'Limón' }, { value: '02', label: 'Pococí' }, { value: '03', label: 'Siquirres' },
    { value: '04', label: 'Talamanca' }, { value: '05', label: 'Matina' }, { value: '06', label: 'Guácimo' },
  ],
};

const DISTRITOS = {
  // San José
  '1-01': [ // San José
    { value: '01', label: 'Carmen' }, { value: '02', label: 'Merced' }, { value: '03', label: 'Hospital' },
    { value: '04', label: 'Catedral' }, { value: '05', label: 'Zapote' }, { value: '06', label: 'San Francisco de Dos Ríos' },
    { value: '07', label: 'Uruca' }, { value: '08', label: 'Mata Redonda' }, { value: '09', label: 'Pavas' },
    { value: '10', label: 'Hatillo' }, { value: '11', label: 'San Sebastián' },
  ],
  '1-02': [ // Escazú
    { value: '01', label: 'Escazú' }, { value: '02', label: 'San Antonio' }, { value: '03', label: 'San Rafael' },
  ],
  '1-03': [ // Desamparados
    { value: '01', label: 'Desamparados' }, { value: '02', label: 'San Miguel' }, { value: '03', label: 'San Juan de Dios' },
    { value: '04', label: 'San Rafael Arriba' }, { value: '05', label: 'San Antonio' }, { value: '06', label: 'Frailes' },
    { value: '07', label: 'Patarrá' }, { value: '08', label: 'San Cristóbal' }, { value: '09', label: 'Rosario' },
    { value: '10', label: 'Damas' }, { value: '11', label: 'San Rafael Abajo' }, { value: '12', label: 'Gravilias' },
    { value: '13', label: 'Los Guido' },
  ],
  '1-08': [ // Goicoechea
    { value: '01', label: 'Guadalupe' }, { value: '02', label: 'San Francisco' }, { value: '03', label: 'Calle Blancos' },
    { value: '04', label: 'Mata de Plátano' }, { value: '05', label: 'Ipís' }, { value: '06', label: 'Rancho Redondo' },
    { value: '07', label: 'Purral' },
  ],
  '1-09': [ // Santa Ana
    { value: '01', label: 'Santa Ana' }, { value: '02', label: 'Salitral' }, { value: '03', label: 'Pozos' },
    { value: '04', label: 'Uruca' }, { value: '05', label: 'Piedades' }, { value: '06', label: 'Brasil' },
  ],
  '1-13': [ // Tibás
    { value: '01', label: 'San Juan' }, { value: '02', label: 'Cinco Esquinas' }, { value: '03', label: 'Anselmo Llorente' },
    { value: '04', label: 'León XIII' }, { value: '05', label: 'Colima' },
  ],
  '1-14': [ // Moravia
    { value: '01', label: 'San Vicente' }, { value: '02', label: 'San Jerónimo' }, { value: '03', label: 'La Trinidad' },
  ],
  '1-15': [ // Montes de Oca
    { value: '01', label: 'San Pedro' }, { value: '02', label: 'Sabanilla' }, { value: '03', label: 'Mercedes' },
    { value: '04', label: 'San Rafael' },
  ],
  '1-18': [ // Curridabat
    { value: '01', label: 'Curridabat' }, { value: '02', label: 'Granadilla' }, { value: '03', label: 'Sánchez' },
    { value: '04', label: 'Tirrases' },
  ],
  // Alajuela
  '2-01': [ // Alajuela
    { value: '01', label: 'Alajuela' }, { value: '02', label: 'San José' }, { value: '03', label: 'Carrizal' },
    { value: '04', label: 'San Antonio' }, { value: '05', label: 'Guácima' }, { value: '06', label: 'San Isidro' },
    { value: '07', label: 'Sabanilla' }, { value: '08', label: 'San Rafael' }, { value: '09', label: 'Río Segundo' },
    { value: '10', label: 'Desamparados' }, { value: '11', label: 'Turrúcares' }, { value: '12', label: 'Tambor' },
    { value: '13', label: 'Garita' }, { value: '14', label: 'Sarapiquí' },
  ],
  '2-03': [ // Grecia
    { value: '01', label: 'Grecia' }, { value: '02', label: 'San Isidro' }, { value: '03', label: 'San José' },
    { value: '04', label: 'San Roque' }, { value: '05', label: 'Tacares' }, { value: '06', label: 'Río Cuarto' },
    { value: '07', label: 'Puente de Piedra' }, { value: '08', label: 'Bolívar' },
  ],
  '2-10': [ // San Carlos
    { value: '01', label: 'Quesada' }, { value: '02', label: 'Florencia' }, { value: '03', label: 'Buenavista' },
    { value: '04', label: 'Aguas Zarcas' }, { value: '05', label: 'Venecia' }, { value: '06', label: 'Pital' },
    { value: '07', label: 'Fortuna' }, { value: '08', label: 'Tigra' }, { value: '09', label: 'Palmera' },
    { value: '10', label: 'Venado' }, { value: '11', label: 'Cutris' }, { value: '12', label: 'Monterrey' },
    { value: '13', label: 'Pocosol' },
  ],
  // Cartago
  '3-01': [ // Cartago
    { value: '01', label: 'Oriental' }, { value: '02', label: 'Occidental' }, { value: '03', label: 'Carmen' },
    { value: '04', label: 'San Nicolás' }, { value: '05', label: 'Aguacaliente' }, { value: '06', label: 'Guadalupe' },
    { value: '07', label: 'Corralillo' }, { value: '08', label: 'Tierra Blanca' }, { value: '09', label: 'Dulce Nombre' },
    { value: '10', label: 'Llano Grande' }, { value: '11', label: 'Quebradilla' },
  ],
  '3-03': [ // La Unión
    { value: '01', label: 'Tres Ríos' }, { value: '02', label: 'San Diego' }, { value: '03', label: 'San Juan' },
    { value: '04', label: 'San Rafael' }, { value: '05', label: 'Concepción' }, { value: '06', label: 'Dulce Nombre' },
    { value: '07', label: 'San Ramón' }, { value: '08', label: 'Río Azul' },
  ],
  // Heredia
  '4-01': [ // Heredia
    { value: '01', label: 'Heredia' }, { value: '02', label: 'Mercedes' }, { value: '03', label: 'San Francisco' },
    { value: '04', label: 'Ulloa' }, { value: '05', label: 'Varablanca' },
  ],
  '4-02': [ // Barva
    { value: '01', label: 'Barva' }, { value: '02', label: 'San Pedro' }, { value: '03', label: 'San Pablo' },
    { value: '04', label: 'San Roque' }, { value: '05', label: 'Santa Lucía' }, { value: '06', label: 'San José de la Montaña' },
  ],
  '4-03': [ // Santo Domingo
    { value: '01', label: 'Santo Domingo' }, { value: '02', label: 'San Vicente' }, { value: '03', label: 'San Miguel' },
    { value: '04', label: 'Paracito' }, { value: '05', label: 'Santo Tomás' }, { value: '06', label: 'Santa Rosa' },
    { value: '07', label: 'Tures' }, { value: '08', label: 'Pará' },
  ],
  '4-07': [ // Belén
    { value: '01', label: 'San Antonio' }, { value: '02', label: 'La Ribera' }, { value: '03', label: 'La Asunción' },
  ],
  // Guanacaste
  '5-01': [ // Liberia
    { value: '01', label: 'Liberia' }, { value: '02', label: 'Cañas Dulces' }, { value: '03', label: 'Mayorga' },
    { value: '04', label: 'Nacascolo' }, { value: '05', label: 'Curubandé' },
  ],
  '5-03': [ // Santa Cruz
    { value: '01', label: 'Santa Cruz' }, { value: '02', label: 'Bolsón' }, { value: '03', label: 'Veintisiete de Abril' },
    { value: '04', label: 'Tempate' }, { value: '05', label: 'Cartagena' }, { value: '06', label: 'Cuajiniquil' },
    { value: '07', label: 'Diriá' }, { value: '08', label: 'Cabo Velas' }, { value: '09', label: 'Tamarindo' },
  ],
  // Puntarenas
  '6-01': [ // Puntarenas
    { value: '01', label: 'Puntarenas' }, { value: '02', label: 'Pitahaya' }, { value: '03', label: 'Chomes' },
    { value: '04', label: 'Lepanto' }, { value: '05', label: 'Paquera' }, { value: '06', label: 'Manzanillo' },
    { value: '07', label: 'Guacimal' }, { value: '08', label: 'Barranca' }, { value: '09', label: 'Monte Verde' },
    { value: '10', label: 'Isla del Coco' }, { value: '11', label: 'Cóbano' }, { value: '12', label: 'Chacarita' },
    { value: '13', label: 'Chira' }, { value: '14', label: 'Acapulco' }, { value: '15', label: 'El Roble' },
    { value: '16', label: 'Arancibia' },
  ],
  '6-06': [ // Quepos
    { value: '01', label: 'Quepos' }, { value: '02', label: 'Savegre' }, { value: '03', label: 'Naranjito' },
  ],
  // Limón
  '7-01': [ // Limón
    { value: '01', label: 'Limón' }, { value: '02', label: 'Valle La Estrella' }, { value: '03', label: 'Río Blanco' },
    { value: '04', label: 'Matama' },
  ],
  '7-02': [ // Pococí
    { value: '01', label: 'Guápiles' }, { value: '02', label: 'Jiménez' }, { value: '03', label: 'Rita' },
    { value: '04', label: 'Roxana' }, { value: '05', label: 'Cariari' }, { value: '06', label: 'Colorado' },
    { value: '07', label: 'La Colonia' },
  ],
};

export default function ConfiguracionEmpresa() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentEmisor, setCurrentEmisor } = useAuthStore();
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return TABS.find(t => t.id === tabFromUrl) ? tabFromUrl : 'general';
  });

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form states
  const [empresa, setEmpresa] = useState({});
  const [haciendaConfig, setHaciendaConfig] = useState({ ambiente: 'development', usuario: '', password: '' });
  const [certificados, setCertificados] = useState([]);
  const [consecutivos, setConsecutivos] = useState([]);
  const [reciclados, setReciclados] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  // Upload states
  const [showUpload, setShowUpload] = useState(false);
  const [uploadData, setUploadData] = useState({ file: null, pin: '', nombre: '' });
  const [uploading, setUploading] = useState(false);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);
  const templateRef = useRef();

  // Actividades económicas state
  const [actividades, setActividades] = useState([]);
  const [consultandoActividades, setConsultandoActividades] = useState(false);

  // Email config state (Office 365 or SMTP)
  const [emailConfig, setEmailConfig] = useState(null);
  const [emailProviderTab, setEmailProviderTab] = useState('office365'); // 'office365' | 'smtp'
  const [emailForm, setEmailForm] = useState({
    // Office 365
    azureTenantId: '',
    azureClientId: '',
    azureClientSecret: '',
    // SMTP
    smtpHost: '',
    smtpPort: '587',
    smtpUser: '',
    smtpPassword: '',
    smtpSecure: false,
    // Common
    fromEmail: '',
    fromName: '',
    replyToEmail: '',
    bccToSender: false
  });
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailTestResult, setEmailTestResult] = useState(null);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // Tenant config state (system settings)
  const [tenantConfig, setTenantConfig] = useState({
    recurring_invoice_hour: '06:00'
  });

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    variant: 'warning'
  });

  const showConfirm = (title, message, onConfirm, variant = 'warning') => {
    setConfirmModal({ isOpen: true, title, message, onConfirm, variant });
  };

  const closeConfirm = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false }));
  };

  // Consultar actividades económicas en Hacienda
  const consultarActividades = async () => {
    if (!empresa.identificacion) {
      toast.error('No hay identificación para consultar');
      return;
    }
    setConsultandoActividades(true);
    try {
      const response = await emisoresApi.consultarHacienda(empresa.identificacion);
      if (response.data?.success && response.data?.data) {
        const data = response.data.data;
        setActividades(data.actividades || []);
        if (data.actividades?.length > 0) {
          toast.success(`Se encontraron ${data.actividades.length} actividades económicas`);
        } else {
          toast('No se encontraron actividades económicas');
        }
      } else {
        toast.error(response.data?.error || 'No se encontró información');
      }
    } catch (error) {
      console.error('Error consultando Hacienda:', error);
      toast.error('Error al consultar Hacienda');
    } finally {
      setConsultandoActividades(false);
    }
  };

  // Load emisor data on mount
  useEffect(() => {
    if (id) {
      // Set emisorId in localStorage so API calls use the correct company
      localStorage.setItem('emisorId', id);
      loadEmisorData(id);
    }
  }, [id]);

  const loadEmisorData = async (emisorId) => {
    setLoading(true);
    try {
      // Load emisor details
      const emisorRes = await settingsApi.getEmisor();
      if (emisorRes.data.data) {
        setEmpresa(emisorRes.data.data);
      }

      // Load hacienda config
      const haciendaRes = await settingsApi.getHaciendaConfig();
      if (haciendaRes.data.data) {
        setHaciendaConfig({
          ambiente: haciendaRes.data.data.ambiente || 'development',
          usuario: haciendaRes.data.data.usuario || '',
          password: ''
        });
      }

      // Load certificates
      const certRes = await settingsApi.getCertificates();
      setCertificados(certRes.data.data || []);

      // Load consecutivos
      const consRes = await settingsApi.getConsecutivos();
      setConsecutivos(consRes.data.data || []);

      // Load recycled consecutivos
      try {
        const recRes = await settingsApi.getConsecutivosReciclados();
        setReciclados(recRes.data.data || []);
      } catch (e) { /* may not have any */ }

      // Load email config
      try {
        const emailRes = await settingsApi.getEmailConfig();
        if (emailRes.data.data) {
          const cfg = emailRes.data.data;
          setEmailConfig(cfg);
          setEmailProviderTab(cfg.provider || 'office365');
          setEmailForm({
            // Office 365
            azureTenantId: cfg.azureTenantId || '',
            azureClientId: cfg.azureClientId || '',
            azureClientSecret: '',
            // SMTP
            smtpHost: cfg.smtpHost || '',
            smtpPort: cfg.smtpPort ? String(cfg.smtpPort) : '587',
            smtpUser: cfg.smtpUser || '',
            smtpPassword: '',
            smtpSecure: cfg.smtpSecure !== undefined ? !!cfg.smtpSecure : false,
            // Common
            fromEmail: cfg.fromEmail || '',
            fromName: cfg.fromName || '',
            replyToEmail: cfg.replyToEmail || '',
            bccToSender: cfg.bccToSender || false
          });
        }
      } catch (e) {
        // Email config might not exist yet
      }

      // Load tenant config (system settings)
      try {
        const tenantRes = await settingsApi.getTenantConfig();
        if (tenantRes.data.data) {
          // Convert from { key: { value, description } } to { key: value }
          const configData = tenantRes.data.data;
          const flatConfig = {};
          for (const [key, obj] of Object.entries(configData)) {
            flatConfig[key] = obj.value || obj;
          }
          setTenantConfig(flatConfig);
        }
      } catch (e) {
        // Tenant config might not exist yet
      }
    } catch (error) {
      console.error('Error loading emisor data:', error);
    } finally {
      setLoading(false);
    }
  };

  // OJO: hoy esta función NO LA LLAMA NADIE (se buscó en todo el archivo). Se
  // deja porque el flujo que debería usarla es legítimo, pero traía dentro una
  // línea rota: `setSelectedEmisor` no existe —no hay ningún estado
  // `selectedEmisor` en el archivo, es un resto de cuando el emisor pasó a vivir
  // en el store—, y leer un identificador no declarado lanza `ReferenceError`.
  // Si alguien la hubiera conectado, el emisor no se habría cambiado nunca: las
  // tres líneas de abajo no llegaban a correr. El store ya hace el trabajo con
  // `setCurrentEmisor`.
  const handleSelectEmisor = (emisor) => {
    setCurrentEmisor(emisor);
    loadEmisorData(emisor.id);
    setActiveTab('general');
  };

  // Save functions for each tab
  const saveGeneral = async () => {
    setSaving(true);
    try {
      const response = await settingsApi.updateEmisor({
        tipoIdentificacion: empresa.tipo_identificacion,
        identificacion: empresa.identificacion,
        nombre: empresa.nombre,
        nombreComercial: empresa.nombre_comercial,
        email: empresa.email,
        telefono: empresa.telefono_numero,
        pais: empresa.pais || 'CR',
        provincia: empresa.provincia,
        canton: empresa.canton,
        distrito: empresa.distrito,
        direccion: empresa.otras_senas,
        actividadEconomica: empresa.actividad_economica
      });
      if (response.data.data) {
        setEmpresa(response.data.data);
        setCurrentEmisor(response.data.data);
      }
      toast.success('Datos guardados');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const saveDiseno = async () => {
    setSaving(true);
    try {
      await settingsApi.updateEmisor({
        logoUrl: empresa.logo_url,
        templateColor: empresa.template_color
      });
      // Update currentEmisor so the new color is reflected immediately
      setCurrentEmisor({
        ...currentEmisor,
        logo_url: empresa.logo_url,
        template_color: empresa.template_color
      });
      toast.success('Diseño guardado');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const saveTerminos = async () => {
    setSaving(true);
    try {
      await settingsApi.updateEmisor({
        terminosCondiciones: empresa.terminos_condiciones
      });
      // Update currentEmisor
      setCurrentEmisor({
        ...currentEmisor,
        terminos_condiciones: empresa.terminos_condiciones
      });
      toast.success('Términos guardados');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const saveHacienda = async () => {
    if (!haciendaConfig.usuario) {
      toast.error('Ingrese el usuario');
      return;
    }
    setSaving(true);
    try {
      await settingsApi.updateHaciendaConfig(haciendaConfig);
      toast.success('Configuración guardada');
      setTestResult(null);
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const testHacienda = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await settingsApi.testHaciendaConnection();
      setTestResult({ success: true, message: response.data.message || 'Conexión exitosa' });
      toast.success('Conexión exitosa');
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.error || 'Error de conexión' });
      toast.error('Error de conexión');
    } finally {
      setTesting(false);
    }
  };

  const handleUploadCert = async (e) => {
    e.preventDefault();
    if (!uploadData.file || !uploadData.pin) {
      toast.error('Seleccione archivo e ingrese PIN');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('p12', uploadData.file);
      formData.append('pin', uploadData.pin);
      if (uploadData.nombre) formData.append('nombre', uploadData.nombre);
      await settingsApi.uploadCertificate(formData);
      toast.success('Certificado subido');
      setShowUpload(false);
      setUploadData({ file: null, pin: '', nombre: '' });
      const certRes = await settingsApi.getCertificates();
      setCertificados(certRes.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al subir');
    } finally {
      setUploading(false);
    }
  };

  const activateCert = async (id) => {
    try {
      await settingsApi.activateCertificate(id);
      toast.success('Certificado activado');
      const certRes = await settingsApi.getCertificates();
      setCertificados(certRes.data.data || []);
    } catch (error) {
      toast.error('Error al activar');
    }
  };

  const deleteCert = (certId) => {
    showConfirm(
      '¿Eliminar certificado?',
      'Esta acción no se puede deshacer.',
      async () => {
        try {
          await settingsApi.deleteCertificate(certId);
          toast.success('Certificado eliminado');
          const certRes = await settingsApi.getCertificates();
          setCertificados(certRes.data.data || []);
        } catch (error) {
          toast.error('Error al eliminar');
        }
      },
      'danger'
    );
  };

  const saveConsecutivos = async () => {
    setSaving(true);
    try {
      await settingsApi.updateConsecutivos({ consecutivos });
      toast.success('Consecutivos guardados');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setEmpresa({ ...empresa, logo_url: e.target.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('es-CR') : '-';

  // Email functions
  const saveEmail = async () => {
    const provider = emailProviderTab;
    // Common validation
    if (!emailForm.fromEmail) {
      toast.error('Ingrese el email remitente');
      return;
    }

    if (provider === 'office365') {
      if (!emailForm.azureTenantId || !emailForm.azureClientId) {
        toast.error('Complete los campos de Office 365');
        return;
      }
      // Secret required on first save or when switching provider
      const needsSecret = !emailConfig || emailConfig.provider !== 'office365' || !emailConfig.hasAzureSecret;
      if (needsSecret && !emailForm.azureClientSecret) {
        toast.error('Ingrese el Client Secret');
        return;
      }
    } else if (provider === 'smtp') {
      if (!emailForm.smtpHost || !emailForm.smtpPort || !emailForm.smtpUser) {
        toast.error('Complete host, puerto y usuario SMTP');
        return;
      }
      const needsPassword = !emailConfig || emailConfig.provider !== 'smtp' || !emailConfig.hasSmtpPassword;
      if (needsPassword && !emailForm.smtpPassword) {
        toast.error('Ingrese la contraseña SMTP');
        return;
      }
    }

    setSaving(true);
    try {
      await settingsApi.saveEmailConfig({ ...emailForm, provider });
      toast.success('Configuración de email guardada');
      setEmailForm({ ...emailForm, azureClientSecret: '', smtpPassword: '' });
      // Reload config
      const emailRes = await settingsApi.getEmailConfig();
      if (emailRes.data.data) {
        setEmailConfig(emailRes.data.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  // SMTP preset helper
  const applySmtpPreset = (preset) => {
    const presets = {
      gmail:   { smtpHost: 'smtp.gmail.com',         smtpPort: '465', smtpSecure: true },
      outlook: { smtpHost: 'smtp.office365.com',     smtpPort: '587', smtpSecure: false },
      zoho:    { smtpHost: 'smtp.zoho.com',          smtpPort: '465', smtpSecure: true },
      cpanel:  { smtpHost: 'mail.suempresa.com',     smtpPort: '465', smtpSecure: true }
    };
    const p = presets[preset];
    if (!p) return;
    setEmailForm(prev => ({ ...prev, ...p }));
  };

  const testEmailConnection = async () => {
    setTestingEmail(true);
    setEmailTestResult(null);
    try {
      const response = await settingsApi.testEmailConnection();
      setEmailTestResult(response.data);
      if (response.data.success) {
        toast.success(response.data.message || 'Conexión exitosa');
      } else {
        toast.error(response.data.error || 'Error de conexión');
      }
    } catch (error) {
      setEmailTestResult({ success: false, error: error.response?.data?.error || error.message });
      toast.error('Error de conexión');
    } finally {
      setTestingEmail(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Ingrese un email de prueba');
      return;
    }
    setSendingTestEmail(true);
    try {
      const response = await settingsApi.sendTestEmail(testEmailAddress);
      if (response.data.success) {
        toast.success(`Email enviado a ${testEmailAddress}`);
        setTestEmailAddress('');
      } else {
        toast.error(response.data.error || 'Error al enviar');
      }
    } catch (error) {
      toast.error('Error al enviar email de prueba');
    } finally {
      setSendingTestEmail(false);
    }
  };

  const deleteEmailConfig = () => {
    showConfirm(
      '¿Eliminar configuración de email?',
      'Deberá configurar nuevamente las credenciales del proveedor de email.',
      async () => {
        try {
          await settingsApi.deleteEmailConfig();
          toast.success('Configuración eliminada');
          setEmailConfig(null);
          setEmailProviderTab('office365');
          setEmailForm({
            azureTenantId: '', azureClientId: '', azureClientSecret: '',
            smtpHost: '', smtpPort: '587', smtpUser: '', smtpPassword: '', smtpSecure: false,
            fromEmail: '', fromName: '', replyToEmail: '', bccToSender: false
          });
        } catch (error) {
          toast.error('Error al eliminar');
        }
      },
      'danger'
    );
  };

  // Save tenant config (system settings)
  const saveTenantConfig = async () => {
    setSaving(true);
    try {
      await settingsApi.updateTenantConfig(tenantConfig);
      toast.success('Configuración del sistema guardada');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  // Render tab content
  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      );
    }

    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo ID</label>
                <select
                  value={empresa.tipo_identificacion || '02'}
                  onChange={(e) => setEmpresa({ ...empresa, tipo_identificacion: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="01">Cédula Física</option>
                  <option value="02">Cédula Jurídica</option>
                  <option value="03">DIMEX</option>
                  <option value="04">NITE</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Identificación</label>
                <input
                  type="text"
                  value={empresa.identificacion || ''}
                  onChange={(e) => setEmpresa({ ...empresa, identificacion: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={async () => {
                    if (!empresa.identificacion) {
                      toast.error('Ingrese una identificación');
                      return;
                    }
                    setConsultandoActividades(true);
                    try {
                      const response = await emisoresApi.consultarHacienda(empresa.identificacion);
                      if (response.data?.success && response.data?.data) {
                        const data = response.data.data;
                        setEmpresa(prev => ({
                          ...prev,
                          nombre: data.nombre || prev.nombre,
                          tipo_identificacion: data.tipoIdentificacion || prev.tipo_identificacion
                        }));
                        setActividades(data.actividades || []);
                        if (data.actividades?.length > 0) {
                          setEmpresa(prev => ({
                            ...prev,
                            actividad_economica: data.actividadPrincipal || prev.actividad_economica
                          }));
                        }
                        toast.success('Datos obtenidos de Hacienda');
                      } else {
                        toast.error(response.data?.error || 'No se encontró información');
                      }
                    } catch (error) {
                      toast.error('Error al consultar Hacienda');
                    } finally {
                      setConsultandoActividades(false);
                    }
                  }}
                  disabled={consultandoActividades}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border-2 border-slate-500 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  {consultandoActividades ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  Consultar Hacienda
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Razón Social *</label>
                <input
                  type="text"
                  value={empresa.nombre || ''}
                  onChange={(e) => setEmpresa({ ...empresa, nombre: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Comercial</label>
                <input
                  type="text"
                  value={empresa.nombre_comercial || ''}
                  onChange={(e) => setEmpresa({ ...empresa, nombre_comercial: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={empresa.email || ''}
                  onChange={(e) => setEmpresa({ ...empresa, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                <input
                  type="text"
                  value={empresa.telefono_numero || ''}
                  onChange={(e) => setEmpresa({ ...empresa, telefono_numero: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Actividad Económica</label>
              <div className="flex gap-2">
                {actividades.length > 0 ? (
                  <select
                    value={empresa.actividad_economica || ''}
                    onChange={(e) => setEmpresa({ ...empresa, actividad_economica: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seleccione...</option>
                    {actividades.map((act) => (
                      <option key={act.codigo} value={act.codigo}>
                        {act.codigo} - {act.descripcion}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={empresa.actividad_economica || ''}
                    onChange={(e) => setEmpresa({ ...empresa, actividad_economica: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ej: 620101"
                  />
                )}
                <button
                  type="button"
                  onClick={consultarActividades}
                  disabled={consultandoActividades}
                  className="flex items-center gap-2 px-4 py-2 border-2 border-slate-500 text-slate-600 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  {consultandoActividades ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  Consultar
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Haga clic en Consultar para obtener las actividades registradas en Hacienda
              </p>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-3">Ubicación</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">País</label>
                  <select
                    value={empresa.pais || 'CR'}
                    onChange={(e) => setEmpresa({ ...empresa, pais: e.target.value, provincia: '', canton: '', distrito: '' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {PAISES.map(p => (
                      <option key={p.value} value={p.value}>{p.flag} {p.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
                  <select
                    value={empresa.provincia || ''}
                    onChange={(e) => setEmpresa({ ...empresa, provincia: e.target.value, canton: '', distrito: '' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seleccione...</option>
                    {PROVINCIAS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cantón</label>
                  <select
                    value={empresa.canton || ''}
                    onChange={(e) => setEmpresa({ ...empresa, canton: e.target.value, distrito: '' })}
                    disabled={!empresa.provincia}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Seleccione...</option>
                    {(CANTONES[empresa.provincia] || []).map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Distrito</label>
                  <select
                    value={empresa.distrito || ''}
                    onChange={(e) => setEmpresa({ ...empresa, distrito: e.target.value })}
                    disabled={!empresa.canton}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Seleccione...</option>
                    {getDistritosOptions(empresa.provincia, empresa.canton).map(d => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                <textarea
                  value={empresa.otras_senas || ''}
                  onChange={(e) => setEmpresa({ ...empresa, otras_senas: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Otras señas de la dirección"
                />
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <button onClick={saveGeneral} disabled={saving} className="flex items-center gap-2 border-2 border-green-500 text-green-600 px-4 py-2 rounded-lg hover:bg-green-50 hover:border-green-600 transition-colors disabled:opacity-50">
                <Save className="h-4 w-4" />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        );

      case 'diseno':
        return (
          <div className="space-y-6">
            {/* Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Logo de la Empresa</label>
              <div className="flex items-start gap-6">
                <div className="w-40 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                  {empresa.logo_url ? (
                    <img src={empresa.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <Image className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  <p className="text-xs text-gray-500 mt-2">PNG, JPG hasta 2MB. Recomendado: 300x100px</p>
                  {empresa.logo_url && (
                    <button
                      onClick={() => setEmpresa({ ...empresa, logo_url: null })}
                      className="text-xs text-red-600 hover:underline mt-1"
                    >
                      Eliminar logo
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Color */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Color Principal del Machote</label>
              <div className="flex items-center gap-4">
                <input
                  type="color"
                  value={empresa.template_color || '#F47920'}
                  onChange={(e) => setEmpresa({ ...empresa, template_color: e.target.value })}
                  className="w-16 h-10 rounded cursor-pointer border border-gray-300"
                />
                <input
                  type="text"
                  value={empresa.template_color || '#F47920'}
                  onChange={(e) => setEmpresa({ ...empresa, template_color: e.target.value })}
                  className="w-28 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                  placeholder="#F47920"
                />
                <div className="flex gap-2">
                  {['#F47920', '#2563EB', '#059669', '#7C3AED', '#DC2626', '#0891B2', '#0F4C5C', '#9B1B1B', '#3B5998', '#1e3a5f'].map(color => (
                    <button
                      key={color}
                      onClick={() => setEmpresa({ ...empresa, template_color: color })}
                      className={clsx(
                        'w-8 h-8 rounded-lg border-2 transition-transform hover:scale-110',
                        empresa.template_color === color ? 'border-gray-900 scale-110' : 'border-transparent'
                      )}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Preview */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-900">Vista Previa del Machote</h3>
                <button
                  onClick={() => setShowPreview(true)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  <Eye className="h-4 w-4" />
                  Ver Completo
                </button>
              </div>
              <div className="border rounded-lg overflow-hidden bg-gray-100 p-4" style={{ maxHeight: '300px', overflow: 'auto' }}>
                <div className="transform scale-50 origin-top-left" style={{ width: '200%' }}>
                  <InvoiceTemplate
                    ref={templateRef}
                    emisor={empresa}
                    receptor={{ nombre: 'Cliente Ejemplo S.A.', tipo_identificacion: '02', identificacion: '3101123456' }}
                    documento={{ tipoDocumento: '01', consecutivo: '00100001010000000001' }}
                    lineas={[
                      { codigo: 'PROD-001', detalle: 'Producto de ejemplo', cantidad: 1, precioUnitario: 100, montoTotalLinea: 100 }
                    ]}
                    totales={{ totalGravado: 100, totalImpuesto: 13, totalComprobante: 113 }}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <button onClick={saveDiseno} disabled={saving} className="flex items-center gap-2 border-2 border-green-500 text-green-600 px-4 py-2 rounded-lg hover:bg-green-50 hover:border-green-600 transition-colors disabled:opacity-50">
                <Save className="h-4 w-4" />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        );

      case 'terminos':
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Términos y Condiciones
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Este texto aparecerá en la parte inferior de todas las facturas y cotizaciones
              </p>
              <textarea
                value={empresa.terminos_condiciones || ''}
                onChange={(e) => setEmpresa({ ...empresa, terminos_condiciones: e.target.value })}
                rows={12}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder={`Ejemplo:

CUENTAS BANCARIAS:
Banco XYZ Colones: CR00000000000000000000
Banco XYZ Dólares: CR00000000000000000000

Esta factura devenga intereses del X% mensual después de su vencimiento.`}
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">Sugerencias:</h4>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• Incluya sus cuentas bancarias para facilitar el pago</li>
                <li>• Agregue políticas de intereses por mora</li>
                <li>• Mencione garantías o condiciones de servicio</li>
                <li>• Incluya información legal relevante</li>
              </ul>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <button onClick={saveTerminos} disabled={saving} className="flex items-center gap-2 border-2 border-green-500 text-green-600 px-4 py-2 rounded-lg hover:bg-green-50 hover:border-green-600 transition-colors disabled:opacity-50">
                <Save className="h-4 w-4" />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        );

      case 'hacienda':
        return (
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Ambiente</label>
              <div className="flex gap-4">
                {['development', 'production'].map(amb => (
                  <label key={amb} className={clsx(
                    'flex items-center gap-3 px-4 py-3 border rounded-lg cursor-pointer transition-colors flex-1',
                    haciendaConfig.ambiente === amb ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                  )}>
                    <input
                      type="radio"
                      value={amb}
                      checked={haciendaConfig.ambiente === amb}
                      onChange={(e) => setHaciendaConfig({ ...haciendaConfig, ambiente: e.target.value })}
                      className="h-4 w-4 text-blue-600"
                    />
                    <div>
                      <p className="font-medium text-gray-900">{amb === 'development' ? 'Desarrollo (Sandbox)' : 'Producción'}</p>
                      <p className="text-xs text-gray-500">{amb === 'development' ? 'Para pruebas' : 'Facturación real'}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-900 mb-4">Credenciales TribuCR</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Usuario *</label>
                  <input
                    type="text"
                    value={haciendaConfig.usuario}
                    onChange={(e) => setHaciendaConfig({ ...haciendaConfig, usuario: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                  <input
                    type="password"
                    value={haciendaConfig.password}
                    onChange={(e) => setHaciendaConfig({ ...haciendaConfig, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                  />
                  <p className="text-xs text-gray-500 mt-1">Deje en blanco para mantener actual</p>
                </div>
              </div>
            </div>

            {testResult && (
              <div className={clsx(
                'p-4 rounded-lg flex items-start gap-3',
                testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              )}>
                {testResult.success ? <CheckCircle className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-red-600" />}
                <div>
                  <p className={clsx('font-medium', testResult.success ? 'text-green-800' : 'text-red-800')}>
                    {testResult.success ? 'Conexión exitosa' : 'Error de conexión'}
                  </p>
                  <p className={clsx('text-sm', testResult.success ? 'text-green-700' : 'text-red-700')}>{testResult.message}</p>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4 border-t">
              <button onClick={testHacienda} disabled={testing || !haciendaConfig.usuario} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                Probar Conexión
              </button>
              <button onClick={saveHacienda} disabled={saving} className="flex items-center gap-2 border-2 border-green-500 text-green-600 px-4 py-2 rounded-lg hover:bg-green-50 hover:border-green-600 transition-colors disabled:opacity-50">
                <Save className="h-4 w-4" />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        );

      case 'certificados':
        return (
          <div className="space-y-6">
            <button onClick={() => setShowUpload(!showUpload)} className="flex items-center gap-2 px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors">
              <Upload className="h-4 w-4" />
              Subir Certificado
            </button>

            {showUpload && (
              <div className="bg-gray-50 rounded-lg p-4 border">
                <form onSubmit={handleUploadCert} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Archivo P12 *</label>
                    <input
                      type="file"
                      accept=".p12,.pfx"
                      onChange={(e) => setUploadData({ ...uploadData, file: e.target.files[0] })}
                      className="w-full"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">PIN *</label>
                      <input
                        type="password"
                        value={uploadData.pin}
                        onChange={(e) => setUploadData({ ...uploadData, pin: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                      <input
                        type="text"
                        value={uploadData.nombre}
                        onChange={(e) => setUploadData({ ...uploadData, nombre: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setShowUpload(false)} className="px-4 py-2 border-2 border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors">Cancelar</button>
                    <button type="submit" disabled={uploading} className="px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50 transition-colors">
                      {uploading ? 'Subiendo...' : 'Subir'}
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="border rounded-lg divide-y">
              {certificados.length === 0 ? (
                <div className="p-8 text-center">
                  <Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No hay certificados</p>
                </div>
              ) : certificados.map(cert => (
                <div key={cert.id} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={clsx('h-10 w-10 rounded-lg flex items-center justify-center', cert.isActive ? 'bg-green-100' : 'bg-gray-100')}>
                      <Shield className={clsx('h-5 w-5', cert.isActive ? 'text-green-600' : 'text-gray-400')} />
                    </div>
                    <div>
                      <p className="font-medium">{(cert.nombre || 'Certificado').replace(/ null$/i, '')}</p>
                      <p className="text-sm text-gray-500">Válido: {formatDate(cert.validFrom)} - {formatDate(cert.validTo)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {cert.isActive ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <CheckCircle className="h-3.5 w-3.5" /> Activo
                      </span>
                    ) : (
                      <button onClick={() => activateCert(cert.id)} className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">Activar</button>
                    )}
                    <button onClick={() => deleteCert(cert.id)} className="p-1.5 hover:bg-red-50 rounded">
                      <Delete className="h-5 w-5 text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'consecutivos':
        return (
          <div className="space-y-6">
            {/* Formato del consecutivo - compacto y elegante */}
            <div className="flex items-center gap-4 p-3 bg-gradient-to-r from-slate-50 to-white border border-slate-200 rounded-lg">
              <span className="text-xs text-slate-500">Formato:</span>
              <div className="flex items-center font-mono text-xs">
                <span className="px-2 py-1 bg-blue-500 text-white rounded-l">001</span>
                <span className="px-2 py-1 bg-emerald-500 text-white">00001</span>
                <span className="px-2 py-1 bg-amber-500 text-white">01</span>
                <span className="px-2 py-1 bg-violet-500 text-white rounded-r">0000000001</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500 ml-2">
                <span><span className="inline-block w-2 h-2 bg-blue-500 rounded mr-1"></span>Sucursal</span>
                <span><span className="inline-block w-2 h-2 bg-emerald-500 rounded mr-1"></span>Terminal</span>
                <span><span className="inline-block w-2 h-2 bg-amber-500 rounded mr-1"></span>Tipo</span>
                <span><span className="inline-block w-2 h-2 bg-violet-500 rounded mr-1"></span>Número</span>
              </div>
            </div>

            {/* Tabla de consecutivos - todos los tipos */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left text-xs font-medium text-slate-500 tracking-wider">
                    <th className="px-4 py-3 w-16">Código</th>
                    <th className="px-4 py-3">Tipo de Documento</th>
                    <th className="px-4 py-3 text-center w-24">Último</th>
                    <th className="px-4 py-3 text-right w-32">Próximo Consecutivo</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {consecutivos.map((cons, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-2.5">
                        <span className="inline-flex items-center justify-center w-7 h-7 bg-slate-100 text-slate-600 rounded text-xs font-mono font-semibold">
                          {cons.tipoDocumento}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-700">
                        {cons.tipoDocumentoNombre}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <input
                          type="text"
                          value={cons.ultimoConsecutivo || 0}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            const updated = [...consecutivos];
                            updated[idx].ultimoConsecutivo = parseInt(val) || 0;
                            setConsecutivos(updated);
                          }}
                          className="w-20 px-2 py-1 border border-slate-200 rounded text-center font-mono text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-400">
                        {cons.sucursal || '001'}-{cons.terminal || '00001'}-{cons.tipoDocumento}-{String((cons.ultimoConsecutivo || 0) + 1).padStart(10, '0')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer con advertencia y botón */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-200">
              <p className="text-xs text-amber-600 flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5" />
                Modificar consecutivos puede causar rechazos en Hacienda
              </p>
              <button onClick={saveConsecutivos} disabled={saving} className="flex items-center gap-2 border-2 border-green-500 text-green-600 px-4 py-2 rounded-lg hover:bg-green-50 hover:border-green-600 transition-colors disabled:opacity-50">
                <Save className="h-4 w-4" />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>

            {/* Consecutivos Reciclados */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">Consecutivos Reciclados</h3>
                  <p className="text-xs text-gray-500">Consecutivos de documentos rechazados que serán reutilizados. Elimine los que no deben reciclarse.</p>
                </div>
                {reciclados.length > 0 && (
                  <button
                    onClick={async () => {
                      if (!confirm('¿Eliminar TODOS los consecutivos reciclados?')) return;
                      try {
                        await settingsApi.deleteAllConsecutivosReciclados();
                        setReciclados([]);
                        toast.success('Reciclados eliminados');
                      } catch { toast.error('Error al eliminar'); }
                    }}
                    className="text-xs px-3 py-1.5 border border-red-400 text-red-600 rounded-lg hover:bg-red-50"
                  >
                    Eliminar todos
                  </button>
                )}
              </div>
              {reciclados.length === 0 ? (
                <p className="text-sm text-gray-400 bg-gray-50 rounded-lg p-4 text-center">No hay consecutivos reciclados pendientes</p>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Tipo</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Consecutivo</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Razón</th>
                        <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Fecha</th>
                        <th className="text-center px-3 py-2 text-xs font-medium text-gray-500">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {reciclados.map(r => (
                        <tr key={r.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                              {r.tipo_documento === '01' ? 'FE' : r.tipo_documento === '03' ? 'NC' : r.tipo_documento === '04' ? 'TE' : r.tipo_documento === '08' ? 'FEC' : r.tipo_documento}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-mono text-xs">{r.numero_consecutivo}</td>
                          <td className="px-3 py-2 text-xs text-gray-500 max-w-[200px] truncate" title={r.razon}>{r.razon || '-'}</td>
                          <td className="px-3 py-2 text-xs text-gray-500">{r.created_at ? new Date(r.created_at).toLocaleDateString('es-CR') : '-'}</td>
                          <td className="px-3 py-2 text-center">
                            <button
                              onClick={async () => {
                                try {
                                  await settingsApi.deleteConsecutivoReciclado(r.id);
                                  setReciclados(prev => prev.filter(x => x.id !== r.id));
                                  toast.success('Reciclado eliminado');
                                } catch { toast.error('Error'); }
                              }}
                              className="text-xs px-2 py-1 border border-red-300 text-red-500 rounded hover:bg-red-50"
                            >
                              Eliminar
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );

      case 'email':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Configuración de Email</h3>
                <p className="text-sm text-gray-500">
                  Seleccione el proveedor de envío de correo: Office 365 (Microsoft Graph) o SMTP convencional.
                  {emailConfig && (
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium">
                      Activo: {emailConfig.provider === 'smtp' ? 'SMTP' : 'Office 365'}
                    </span>
                  )}
                </p>
              </div>
              {emailConfig && (
                <button onClick={deleteEmailConfig} className="p-2 hover:bg-red-50 rounded-lg" title="Eliminar configuración">
                  <Delete className="h-5 w-5 text-red-500" />
                </button>
              )}
            </div>

            {/* Sub-tabs: Office 365 / SMTP */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex gap-6">
                {[
                  { id: 'office365', label: 'Office 365' },
                  { id: 'smtp', label: 'SMTP' }
                ].map(t => (
                  <button
                    key={t.id}
                    onClick={() => setEmailProviderTab(t.id)}
                    className={clsx(
                      'py-2 px-1 border-b-2 text-sm font-medium transition-colors',
                      emailProviderTab === t.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    )}
                  >
                    {t.label}
                    {emailConfig?.provider === t.id && (
                      <span className="ml-2 inline-block w-2 h-2 rounded-full bg-green-500" title="Proveedor activo" />
                    )}
                  </button>
                ))}
              </nav>
            </div>

            {/* Office 365 tab */}
            {emailProviderTab === 'office365' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Azure Tenant ID *</label>
                    <input
                      type="text"
                      value={emailForm.azureTenantId}
                      onChange={(e) => setEmailForm({ ...emailForm, azureTenantId: e.target.value })}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Azure Client ID (App ID) *</label>
                    <input
                      type="text"
                      value={emailForm.azureClientId}
                      onChange={(e) => setEmailForm({ ...emailForm, azureClientId: e.target.value })}
                      placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Azure Client Secret *</label>
                  <div className="relative">
                    <input
                      type={showClientSecret ? 'text' : 'password'}
                      value={emailForm.azureClientSecret}
                      onChange={(e) => setEmailForm({ ...emailForm, azureClientSecret: e.target.value })}
                      placeholder={emailConfig?.hasAzureSecret ? '••••••••••••••••' : 'Ingrese el client secret'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowClientSecret(!showClientSecret)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                    >
                      {showClientSecret ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {emailConfig?.hasAzureSecret ? 'Deje en blanco para mantener el secret actual' : 'Secret de la aplicación en Azure AD'}
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">¿Cómo configurar Azure AD?</h4>
                  <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                    <li>Ve a Azure Portal → Azure Active Directory → App registrations</li>
                    <li>Crea una nueva aplicación</li>
                    <li>En "API permissions", agrega Microsoft Graph → Mail.Send (Application)</li>
                    <li>Concede consentimiento de administrador</li>
                    <li>En "Certificates & secrets", crea un nuevo client secret</li>
                    <li>Copia el Tenant ID, Application ID y Client Secret aquí</li>
                  </ol>
                </div>
              </div>
            )}

            {/* SMTP tab */}
            {emailProviderTab === 'smtp' && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ajustes rápidos</label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'gmail',   label: 'Gmail' },
                      { id: 'outlook', label: 'Outlook/Office 365' },
                      { id: 'zoho',    label: 'Zoho' },
                      { id: 'cpanel',  label: 'cPanel / Hosting' }
                    ].map(p => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => applySmtpPreset(p.id)}
                        className="px-3 py-1.5 text-xs border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700"
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Autocompleta host y puerto. Ajuste usuario/contraseña manualmente.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Servidor SMTP (Host) *</label>
                    <input
                      type="text"
                      value={emailForm.smtpHost}
                      onChange={(e) => setEmailForm({ ...emailForm, smtpHost: e.target.value })}
                      placeholder="smtp.miempresa.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Puerto *</label>
                    <input
                      type="number"
                      value={emailForm.smtpPort}
                      onChange={(e) => setEmailForm({ ...emailForm, smtpPort: e.target.value })}
                      placeholder="587"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Usuario *</label>
                    <input
                      type="text"
                      value={emailForm.smtpUser}
                      onChange={(e) => setEmailForm({ ...emailForm, smtpUser: e.target.value })}
                      placeholder="facturacion@miempresa.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña *</label>
                    <div className="relative">
                      <input
                        type={showSmtpPassword ? 'text' : 'password'}
                        value={emailForm.smtpPassword}
                        onChange={(e) => setEmailForm({ ...emailForm, smtpPassword: e.target.value })}
                        placeholder={emailConfig?.hasSmtpPassword ? '••••••••••••••••' : 'Ingrese la contraseña'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      >
                        {showSmtpPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {emailConfig?.hasSmtpPassword ? 'Deje en blanco para mantener la contraseña actual' : 'Contraseña de la cuenta o app password'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <input
                    type="checkbox"
                    id="smtpSecure"
                    checked={emailForm.smtpSecure || false}
                    onChange={(e) => setEmailForm({ ...emailForm, smtpSecure: e.target.checked })}
                    className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="smtpSecure" className="text-sm text-gray-700 cursor-pointer">
                    <span className="font-medium">Conexión SSL/TLS directa</span>
                    <span className="text-gray-500 ml-1">— Active para puerto 465. Desactive para puerto 587 con STARTTLS.</span>
                  </label>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">Notas sobre SMTP</h4>
                  <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                    <li><strong>Gmail:</strong> requiere generar una "App Password" (no sirve la contraseña normal si tiene 2FA).</li>
                    <li><strong>Outlook/Office 365:</strong> si tiene Office 365, se recomienda usar la pestaña Office 365 (más confiable).</li>
                    <li><strong>Puerto 465:</strong> marque SSL/TLS directa. <strong>Puerto 587:</strong> desmarque (usa STARTTLS).</li>
                  </ul>
                </div>
              </div>
            )}

            {/* Common fields: sender identity */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Configuración de Envío</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Remitente *</label>
                  <input
                    type="email"
                    value={emailForm.fromEmail}
                    onChange={(e) => setEmailForm({ ...emailForm, fromEmail: e.target.value })}
                    placeholder="facturacion@miempresa.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {emailProviderTab === 'smtp'
                      ? 'Dirección que aparecerá como remitente (puede ser igual al usuario SMTP)'
                      : 'Buzón de Office 365 para enviar facturas'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Remitente</label>
                  <input
                    type="text"
                    value={emailForm.fromName}
                    onChange={(e) => setEmailForm({ ...emailForm, fromName: e.target.value })}
                    placeholder="Facturación Electrónica"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Reply-To (opcional)</label>
                <input
                  type="email"
                  value={emailForm.replyToEmail || ''}
                  onChange={(e) => setEmailForm({ ...emailForm, replyToEmail: e.target.value })}
                  placeholder="soporte@miempresa.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Si lo deja en blanco, las respuestas irán al email remitente</p>
              </div>
            </div>

            {/* BCC option */}
            <div className="flex items-center gap-3 mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                id="bccToSender"
                checked={emailForm.bccToSender || false}
                onChange={(e) => setEmailForm({ ...emailForm, bccToSender: e.target.checked })}
                className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="bccToSender" className="text-sm text-gray-700 cursor-pointer">
                <span className="font-medium">Activar copia de envío (BCC)</span>
                <span className="text-gray-500 ml-1">— Cada email enviado a clientes también se copiará a la cuenta emisora ({emailForm.fromEmail || 'sin configurar'})</span>
              </label>
            </div>

            {emailTestResult && (
              <div className={clsx(
                'p-4 rounded-lg flex items-start gap-3',
                emailTestResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
              )}>
                {emailTestResult.success ? <CheckCircle className="h-5 w-5 text-green-600" /> : <XCircle className="h-5 w-5 text-red-600" />}
                <div>
                  <p className={clsx('font-medium', emailTestResult.success ? 'text-green-800' : 'text-red-800')}>
                    {emailTestResult.success ? 'Conexión exitosa' : 'Error de conexión'}
                  </p>
                  <p className={clsx('text-sm', emailTestResult.success ? 'text-green-700' : 'text-red-700')}>
                    {emailTestResult.success ? emailTestResult.message : emailTestResult.error}
                  </p>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4 border-t">
              <button
                onClick={testEmailConnection}
                disabled={testingEmail || !emailConfig}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                {testingEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                Probar Conexión
              </button>
              <button onClick={saveEmail} disabled={saving} className="flex items-center gap-2 border-2 border-green-500 text-green-600 px-4 py-2 rounded-lg hover:bg-green-50 hover:border-green-600 transition-colors disabled:opacity-50">
                <Save className="h-4 w-4" />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>

            {/* Send Test Email */}
            {emailConfig && (
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Enviar Email de Prueba</h4>
                <div className="flex gap-3">
                  <input
                    type="email"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder="email@ejemplo.com"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={sendTestEmail}
                    disabled={sendingTestEmail || !testEmailAddress}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {sendingTestEmail ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Enviar
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 'sistema':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Configuración del Sistema</h3>
              <p className="text-sm text-gray-500">Ajustes de automatización y procesos programados</p>
            </div>

            {/* Hora de generación de facturas recurrentes */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">Generación Automática de Facturas Recurrentes</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Define la hora del día en que se generarán automáticamente las facturas recurrentes programadas.
                  </p>
                  <div className="mt-3 flex items-center gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Hora (Costa Rica)</label>
                      <select
                        value={empresa.recurring_hour || '06:00'}
                        onChange={(e) => setEmpresa({ ...empresa, recurring_hour: e.target.value })}
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="00:00">12:00 AM</option>
                        <option value="01:00">1:00 AM</option>
                        <option value="02:00">2:00 AM</option>
                        <option value="03:00">3:00 AM</option>
                        <option value="04:00">4:00 AM</option>
                        <option value="05:00">5:00 AM</option>
                        <option value="06:00">6:00 AM</option>
                        <option value="07:00">7:00 AM</option>
                        <option value="08:00">8:00 AM</option>
                        <option value="09:00">9:00 AM</option>
                        <option value="10:00">10:00 AM</option>
                        <option value="11:00">11:00 AM</option>
                        <option value="12:00">12:00 PM</option>
                        <option value="13:00">1:00 PM</option>
                        <option value="14:00">2:00 PM</option>
                        <option value="15:00">3:00 PM</option>
                        <option value="16:00">4:00 PM</option>
                        <option value="17:00">5:00 PM</option>
                        <option value="18:00">6:00 PM</option>
                        <option value="19:00">7:00 PM</option>
                        <option value="20:00">8:00 PM</option>
                        <option value="21:00">9:00 PM</option>
                        <option value="22:00">10:00 PM</option>
                        <option value="23:00">11:00 PM</option>
                      </select>
                    </div>
                    <div className="text-xs text-gray-500 mt-4">
                      El sistema verificará cada hora si hay facturas recurrentes pendientes y las generará a la hora configurada.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-amber-800">Nota importante</h4>
                  <p className="text-sm text-amber-700 mt-1">
                    Las facturas recurrentes se generan automáticamente según la frecuencia configurada en cada plantilla.
                    Esta hora determina el momento del día en que el sistema procesará las facturas pendientes.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <button onClick={async () => {
                setSaving(true);
                try {
                  await settingsApi.updateEmisor({ recurringHour: empresa.recurring_hour || '06:00' });
                  toast.success('Hora de generación guardada');
                } catch (e) { toast.error('Error al guardar'); }
                setSaving(false);
              }} disabled={saving} className="flex items-center gap-2 px-4 py-2 border-2 border-green-500 text-green-600 rounded-lg hover:bg-green-50 hover:border-green-600 transition-colors disabled:opacity-50">
                <Save className="h-4 w-4" />
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        );

      case 'integraciones':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Integraciones / Módulos Externos</h3>
              <p className="text-sm text-gray-500">Sistemas externos que se integran (embebidos) dentro de Finance 360.</p>
            </div>

            {/* Planillas K360 */}
            <div className="border rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                  <Plug className="h-5 w-5 text-violet-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">Módulo Planillas — Payroll360 Lite</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Activa el módulo <strong>Planillas</strong> en el menú lateral. Embebe el sistema <strong>Payroll360 Lite</strong> dentro de Finance 360. Los usuarios verán "Planillas" solo si su rol tiene el permiso correspondiente <strong>y</strong> este toggle está activo.
                  </p>

                  <label className={`mt-4 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${empresa.planillas_enabled ? 'border-violet-300 bg-violet-50 text-violet-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                    <input
                      type="checkbox"
                      checked={!!empresa.planillas_enabled}
                      onChange={(e) => setEmpresa({ ...empresa, planillas_enabled: e.target.checked })}
                      className="accent-violet-600 w-4 h-4"
                    />
                    Activar módulo Planillas (Payroll360 Lite embebido)
                  </label>

                  {empresa.planillas_enabled && (
                    <div className="mt-3 space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          URL del sistema Payroll360 Lite (HTTPS)
                        </label>
                        <input
                          type="url"
                          value={empresa.planillas_url || ''}
                          onChange={(e) => setEmpresa({ ...empresa, planillas_url: e.target.value })}
                          placeholder="https://payroll360.tu-empresa.com"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 text-sm"
                        />
                        <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-500">
                          <ExternalLink className="h-3 w-3" />
                          Iframe principal. Si SSO está configurado abajo, el login es automático; si no, manual.
                        </div>
                      </div>

                      {/* === SSO (autologin) === */}
                      <div className="border-t pt-3 mt-3">
                        <h5 className="text-xs font-semibold text-gray-700 mb-1">SSO — Autologin (opcional)</h5>
                        <p className="text-[11px] text-gray-500 mb-2">
                          Con SSO configurado, el usuario entra a Planillas sin volver a loguearse. Finance 360 firma un JWT con el shared secret y el email del usuario; Payroll360 Lite lo valida y autologuea por email.
                        </p>
                        <div className="space-y-2">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Endpoint SSO (URL del callback en Payroll360)
                            </label>
                            <input
                              type="url"
                              value={empresa.planillas_sso_url || ''}
                              onChange={(e) => setEmpresa({ ...empresa, planillas_sso_url: e.target.value })}
                              placeholder="https://payroll.kuantabridge.com/api/auth/sso"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 text-sm font-mono"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Shared Secret (para firmar el JWT)
                            </label>
                            <input
                              type="password"
                              value={empresa.planillas_sso_secret || ''}
                              onChange={(e) => setEmpresa({ ...empresa, planillas_sso_secret: e.target.value })}
                              placeholder={empresa.planillas_sso_configured ? '••••••••• (ya configurado — escriba para reemplazar)' : 'el secret compartido con Payroll360'}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 text-sm font-mono"
                              autoComplete="new-password"
                            />
                            <div className="mt-1 text-[11px] text-gray-500">
                              Se guarda solo en backend y se usa para firmar el token SSO. El payload del JWT incluye <code className="font-mono">email</code> + <code className="font-mono">appCode</code>; expira en 60s.
                            </div>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              App Code <span className="text-gray-400 font-normal">(default: payroll)</span>
                            </label>
                            <input
                              type="text"
                              value={empresa.planillas_sso_app_code || ''}
                              onChange={(e) => setEmpresa({ ...empresa, planillas_sso_app_code: e.target.value })}
                              placeholder="payroll"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 text-sm font-mono"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-blue-800">Permisos por rol</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    Recordá asignar el permiso <strong>"Planillas → Ver módulo"</strong> al rol del usuario en <strong>Configuración → Roles</strong>. Sin el permiso, no verán "Planillas" en el menú aunque el toggle esté activo.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t">
              <button onClick={async () => {
                setSaving(true);
                try {
                  const payload = {
                    planillasEnabled: !!empresa.planillas_enabled,
                    planillasUrl: empresa.planillas_url || '',
                    planillasSsoUrl: empresa.planillas_sso_url || '',
                    planillasSsoAppCode: empresa.planillas_sso_app_code || 'payroll'
                  };
                  // Solo mandamos el secret si el user lo escribió en esta sesión (string no vacío).
                  // Si lo dejó vacío, NO sobrescribir el existente.
                  if (empresa.planillas_sso_secret && empresa.planillas_sso_secret.trim()) {
                    payload.planillasSsoSecret = empresa.planillas_sso_secret.trim();
                  }
                  await settingsApi.updateEmisor(payload);
                  // Limpiar el input del secret después de guardar (no queremos dejarlo a la vista)
                  setEmpresa({ ...empresa, planillas_sso_secret: '' });
                  // Refrescar emisor en authStore para que el sidebar reaccione al instante
                  try { await useAuthStore.getState().refreshUser?.(); } catch {}
                  toast.success('Integración guardada');
                } catch (e) {
                  toast.error(e.response?.data?.error || 'Error al guardar');
                }
                setSaving(false);
              }} disabled={saving} className="flex items-center gap-2 px-4 py-2 border-2 border-violet-500 text-violet-600 rounded-lg hover:bg-violet-50 hover:border-violet-600 disabled:opacity-50">
                <Save className="h-4 w-4" />
                {saving ? 'Guardando...' : 'Guardar Integración'}
              </button>
            </div>
          </div>
        );

      case 'recepcion':
        return <RecepcionFacturas emisorId={id} />;

      case 'notificaciones':
        return <NotificacionesSubscribersTab />;

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header with back button */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/configuracion/empresa')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{empresa.nombre || 'Configuración'}</h1>
          <p className="text-sm text-gray-500">{empresa.identificacion}</p>
        </div>
      </div>

      {/* Tabs and content */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Tabs */}
        <div className="border-b overflow-x-auto">
          <div className="flex">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={clsx(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors',
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                )}
              >
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Vista Previa del Machote</h2>
              <button onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-gray-600">
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-80px)] bg-gray-100">
              <InvoiceTemplate
                emisor={empresa}
                receptor={{ nombre: 'Cliente Ejemplo S.A.', tipo_identificacion: '02', identificacion: '3101123456', email: 'cliente@ejemplo.com' }}
                documento={{ tipoDocumento: '01', consecutivo: '00100001010000000001', clave: '50601012600310270601300100001010000000001199999999' }}
                lineas={[
                  { codigo: 'PROD-001', detalle: 'Servicio de Consultoría', cantidad: 10, precioUnitario: 150, montoTotalLinea: 1500 },
                  { codigo: 'PROD-002', detalle: 'Licencia de Software Anual', cantidad: 1, precioUnitario: 500, montoTotalLinea: 500 },
                ]}
                totales={{ totalGravado: 2000, totalImpuesto: 260, totalComprobante: 2260 }}
                tipoCambio={500}
              />
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirm}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        confirmText="Sí, eliminar"
        cancelText="Cancelar"
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Tab: Notificaciones — matriz user × tipo de evento (admin marca subscriptores)
// ═══════════════════════════════════════════════════════════════════════════
function NotificacionesSubscribersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({}); // { 'userId:tipo': true }

  const TIPOS = [
    { key: 'cobro_cxc', label: 'Cobros recibidos (CxC)', desc: 'Cuando se aplica un cobro a una factura en CxC.' },
    { key: 'pago_cxp',  label: 'Pagos a proveedores (CxP)', desc: 'Cuando se aplica un pago a una factura en CxP.' },
    { key: 'presupuesto_alerta', label: 'Alertas del presupuesto', desc: 'Cuando una cuenta o un departamento llega al 80, 90 o 100 % de lo presupuestado. También por correo.' }
  ];

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await notificationsApi.listSubscribers();
      setUsers(res.data?.data || []);
    } catch (e) {
      toast.error('Error cargando usuarios');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const toggle = async (userId, tipo, current) => {
    const key = `${userId}:${tipo}`;
    setUpdating(u => ({ ...u, [key]: true }));
    // Optimistic update
    setUsers(prev => prev.map(u => u.id === userId
      ? { ...u, subscriptions: { ...u.subscriptions, [tipo]: !current } }
      : u
    ));
    try {
      await notificationsApi.updateSubscriber(userId, tipo, !current);
    } catch (e) {
      // Revert
      setUsers(prev => prev.map(u => u.id === userId
        ? { ...u, subscriptions: { ...u.subscriptions, [tipo]: current } }
        : u
      ));
      toast.error('Error al actualizar suscripción');
    } finally {
      setUpdating(u => { const c = { ...u }; delete c[key]; return c; });
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2"><Bell className="h-5 w-5" />Suscripciones de notificaciones</h3>
        <p className="text-sm text-gray-500 mt-1">
          Elegí qué usuarios reciben la <strong>notificación in-app</strong> (campana del header) por cada tipo de evento.
          El usuario que <em>aplica</em> el evento NO recibe la notificación (no necesita avisarse a sí mismo).
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-gray-500">Cargando...</div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center text-sm text-gray-500">No hay usuarios asignados a esta empresa</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Usuario</th>
                {TIPOS.map(t => (
                  <th key={t.key} className="px-4 py-3 text-center text-xs font-semibold text-gray-600">
                    {t.label}
                    <div className="text-[10px] font-normal text-gray-400 mt-0.5 max-w-[160px] mx-auto">{t.desc}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900">{u.name} {u.lastName || ''}</p>
                    <p className="text-xs text-gray-500">{u.email}</p>
                  </td>
                  {TIPOS.map(t => {
                    const k = `${u.id}:${t.key}`;
                    const checked = !!u.subscriptions?.[t.key];
                    return (
                      <td key={t.key} className="px-4 py-3 text-center">
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={updating[k]}
                            onChange={() => toggle(u.id, t.key, checked)}
                            className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                          />
                        </label>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
